import { Button, cn } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/**
 * The narrowing mode a {@link ContextBand} represents (frontend-architecture.md §2):
 *
 * - `step` — a higher-role viewer is *viewing* a narrower plane they already own
 *   (no identity change, no tenant crossing). Pure client navigation state; ships
 *   live.
 * - `enter` — a platform operator operating *inside* a specific tenant. Needs the
 *   audited cross-tenant path.
 * - `actas` — login-as: the operator has assumed a user's identity.
 *
 * `enter` and `actas` are **blocked on AD-5** (the impersonation ADR, ADR-0064,
 * still Proposed): they are typed so the shell can carry them, but render inert —
 * never a fabricated cross-tenant or impersonation session.
 */
export type ContextBandMode = 'step' | 'enter' | 'actas'

/** The `step` band: presentational copy + a Back action the consumer wires. */
export interface ContextBandStepProps {
	mode: 'step'
	/** The band's leading text (e.g. "Viewing a narrower plane"). */
	title?: string
	/** The accessible label of the Back action. */
	action?: string
	/** Invoked when the action is activated; the band performs no navigation itself. */
	onAction?: () => void
	/** Extra classes merged onto the band container. */
	className?: string
}

/** The AD-5-gated `enter`/`actas` band: inert, capability-blocked. */
export interface ContextBandBlockedProps {
	mode: 'enter' | 'actas'
	/** Overrides the default capability-blocked note. */
	blockedReason?: string
	/** Extra classes merged onto the band container. */
	className?: string
}

/**
 * Props for {@link ContextBand}, a discriminated union on `mode` so step-only props
 * (`title`/`action`/`onAction`) cannot be passed to the inert `enter`/`actas` modes,
 * and vice-versa — misuse is a compile error, not a silently-ignored prop.
 */
export type ContextBandProps = ContextBandStepProps | ContextBandBlockedProps

/** Default note shown for the AD-5-gated modes until the impersonation capability exists. */
const BLOCKED_NOTE = 'Requires the impersonation capability (AD-5) — not yet available.'

/**
 * Triangle-alert glyph marking the band. Decorative — the `role="status"` region and
 * its text carry the meaning for assistive tech, so the icon is `aria-hidden`.
 */
function AlertIcon(): ReactElement {
	return (
		<span data-testid="context-band-icon" aria-hidden="true" className="shrink-0">
			<svg
				width="16"
				height="16"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				aria-hidden="true"
			>
				<path d="M12 9v4" strokeLinecap="round" />
				<path d="M12 17h.01" strokeLinecap="round" />
				<path
					d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"
					strokeLinejoin="round"
				/>
			</svg>
		</span>
	)
}

/**
 * The context band (AD-4, frontend-architecture.md §2): the red status bar a
 * per-plane shell renders above its content when a viewer is narrowing into a plane
 * below their natural one. Mounted through {@link PlaneShellFrame}'s `band` slot.
 *
 * @remarks
 * - **`step` ships live.** It shows the title and a Back action that calls
 *   `onAction`; the band itself never navigates (the shell supplies the callback),
 *   so the component holds no routing and no plane data — it stays below the
 *   ui-layer boundary (ADR-0031).
 * - **`enter`/`actas` fail closed.** They are typed for the shell but render a
 *   **disabled** affordance plus a note that the impersonation capability (AD-5,
 *   ADR-0064) is unavailable. They invoke no callback and fabricate no session —
 *   the impersonation gap stays closed until AD-5 lands.
 * - **Announced:** the container is `role="status"` so a screen reader notes the
 *   narrowing; the icon is decorative (`aria-hidden`).
 * - **Brand-agnostic:** every class is a `ds-*` token utility; the red uses the
 *   danger token (`bg-ds-danger`/`text-ds-on-danger`), like `chip`/`tag` danger.
 *
 * @see packages/ui-domain/src/context-band/context-band.stories.tsx for visual examples
 */
export function ContextBand(props: ContextBandProps): ReactElement {
	const container = cn(
		'flex flex-wrap items-center gap-ds-3 bg-ds-danger px-ds-gutter py-ds-2 text-ds-sm text-ds-on-danger',
		props.className,
	)

	if (props.mode === 'step') {
		return (
			<div role="status" data-band-mode="step" className={container}>
				<AlertIcon />
				<span className="font-medium">{props.title}</span>
				<Button variant="danger" appearance="outline" size="sm" className="ml-auto" onPress={props.onAction}>
					{props.action}
				</Button>
			</div>
		)
	}

	// enter / actas — typed but inert: no interactive element at all, just the AD-5
	// blocked note. There is nothing to click, tab to, or re-enable via CSS.
	return (
		<div role="status" data-band-mode={props.mode} className={container}>
			<AlertIcon />
			<span>{props.blockedReason ?? BLOCKED_NOTE}</span>
		</div>
	)
}
